import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  Flex,
  ModalFooter,
  FlexProps,
} from '@everlywell/ui-kit';
import PowerReviewsDisplay from 'components/PDP/PowerReviewsDisplay';
import React, { useEffect } from 'react';
import { track } from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';

import usePowerReviews, {
  REVIEW_DISPLAY_NODE,
} from '../../hooks/usePowerReviews';

type SlotOverride = {
  sx?: any;
};

export type ReviewsContainerSlotOverrides = {
  root?: SlotOverride;
  button?: SlotOverride;
  modalOverlay?: SlotOverride;
  modalContent?: SlotOverride;
  modalHeader?: SlotOverride;
  modalCloseButton?: SlotOverride;
  modalBody?: SlotOverride;
  modalFooter?: SlotOverride;
};

export type ReviewsContainerProps = FlexProps & {
  productId: string;
  name: string;
  buttonText?: string;
  slots?: ReviewsContainerSlotOverrides;
};

const mergeSlotSx = (
  ...sxValues: Array<
    ReviewsContainerProps['sx'] | Record<string, any> | undefined
  >
): ReviewsContainerProps['sx'] => {
  const merged = sxValues.reduce<Record<string, any>>((acc, value) => {
    if (!value) {
      return acc;
    }

    if (Array.isArray(value)) {
      return value.reduce<Record<string, any>>((innerAcc, item) => {
        if (!item || typeof item === 'function') {
          return innerAcc;
        }

        if (Array.isArray(item)) {
          return {
            ...innerAcc,
            ...item.reduce<Record<string, any>>((nestedAcc, nestedItem) => {
              if (!nestedItem || typeof nestedItem === 'function') {
                return nestedAcc;
              }

              return {
                ...nestedAcc,
                ...nestedItem,
              };
            }, {}),
          };
        }

        return {
          ...innerAcc,
          ...item,
        };
      }, acc);
    }

    if (typeof value === 'object') {
      return {
        ...acc,
        ...value,
      };
    }

    return acc;
  }, {});

  return Object.keys(merged).length ? merged : undefined;
};

const ReviewsContainer = ({
  productId,
  name,
  buttonText = 'More reviews',
  slots,
  sx,
  ...rest
}: ReviewsContainerProps) => {
  const { renderPowerReviews } = usePowerReviews(productId);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const tryRender = () => {
      const el = document.getElementById(REVIEW_DISPLAY_NODE);

      if (el && !cancelled) {
        renderPowerReviews();
      } else if (!cancelled) {
        requestAnimationFrame(tryRender);
      }
    };

    tryRender();

    track({
      event: ANALYTICS.EVENTS.OPEN_REVIEWS_MODAL,
      data: {
        name: name || '',
        productId: productId || 0,
        cta: buttonText,
      },
    });

    return () => {
      cancelled = true;
    };
  }, [buttonText, isOpen, name, productId, renderPowerReviews]);

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      {...rest}
      sx={mergeSlotSx(sx, slots?.root?.sx)}
    >
      <Button
        onClick={onOpen}
        marginBlock="40px"
        variant="secondary"
        colorScheme="greenCS"
        data-testid="viewAllReviews"
        sx={slots?.button?.sx}
      >
        {buttonText}
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={['full', null, null, '3xl']}
      >
        <ModalOverlay sx={slots?.modalOverlay?.sx} />
        <ModalContent
          backgroundColor="tints.white"
          sx={slots?.modalContent?.sx}
        >
          <ModalHeader sx={slots?.modalHeader?.sx} display="block !important">
            {name}
          </ModalHeader>
          <ModalCloseButton sx={slots?.modalCloseButton?.sx} />
          <ModalBody
            maxHeight={['100vh', null, null, '80vh']}
            sx={slots?.modalBody?.sx}
          >
            <PowerReviewsDisplay id={REVIEW_DISPLAY_NODE} />
          </ModalBody>
          <ModalFooter sx={slots?.modalFooter?.sx} display="flex !important">
            &nbsp;
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default ReviewsContainer;
